import {Alert, Button, Card, FormControl, FormField} from 'platform/components';
import {HStack, Show, Text, VStack} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {isEmpty, isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';

import {Nullish, RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

import {RECORDING_SETTINGS_SECTION_ID} from '../../../constants/RECORDING_SETTINGS_SECTION_ID';
import {
  ROUTING_MESSAGE_MAX_LENGTH,
  TelephonyLineRoutingFormValues,
} from '../utils/telephonyLineRoutingFormSchema';

type MessageFieldName = 'closedMessage' | 'noAnswerMessage';

const KIND_FIELD_NAME = {
  closedMessage: 'closedKind',
  noAnswerMessage: 'noAnswerKind',
} as const satisfies Record<MessageFieldName, keyof TelephonyLineRoutingFormValues>;

interface TelephonyLineRoutingFallbackCardProps extends RequiredTestIdProps {
  control: FormControl<TelephonyLineRoutingFormValues>;
  formApi: UseFormReturn<TelephonyLineRoutingFormValues>;
  name: MessageFieldName;
  title: string;
  description: string;
  greetingOutOfHours: string | Nullish;
  /** The tenant never saved its recording settings, so the BE refuses a bot fallback. */
  isRecordingSettingsMissing: boolean;
  isReadOnly: boolean;
}

const MESSAGE_MIN_ROWS = 3;

export function TelephonyLineRoutingFallbackCard(props: TelephonyLineRoutingFallbackCardProps) {
  const navigate = useNavigate();
  const kindFieldName = KIND_FIELD_NAME[props.name];
  const isBot = useWatch({control: props.formApi.control, name: kindFieldName}) === 'bot';

  const handleCopyGreeting = () => {
    const greeting = props.greetingOutOfHours;

    if (isNil(greeting) || isEmpty(greeting)) {
      return;
    }

    props.formApi.setValue(props.name, greeting, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <Card title={props.title} data-testid={suffixTestId('card', props)}>
      <VStack spacing={4}>
        <Text size="small" color="secondary">
          {props.description}
        </Text>
        <FormField
          control={props.control}
          type="radio"
          name={kindFieldName}
          isDisabled={props.isReadOnly}
          options={[
            {
              label: i18n.t('page.settings.telephony.routingFallbackMessage'),
              value: 'message',
            },
            {
              label: i18n.t('page.settings.telephony.routingFallbackBot'),
              value: 'bot',
            },
          ]}
          data-testid={suffixTestId('kind', props)}
        />
        <Show when={isBot}>
          <Text size="small" color="secondary">
            {i18n.t('page.settings.telephony.routingFallbackBotMessageHint')}
          </Text>
        </Show>
        <Show when={isBot && props.isRecordingSettingsMissing}>
          <Alert
            type="inline"
            variant="warning"
            message={i18n.t('page.settings.telephony.recordingSettingsRequired')}
            hyperlinks={[
              {
                title: i18n.t('page.settings.telephony.recordingSettingsLink'),
                onClick: () =>
                  navigate(`${settingsRoutes.telephony}#${RECORDING_SETTINGS_SECTION_ID}`),
              },
            ]}
            data-testid={suffixTestId('recordingSettingsRequired', props)}
          />
        </Show>
        <FormField
          control={props.control}
          type="textarea"
          name={props.name}
          label={i18n.t('page.settings.telephony.routingMessageLabel')}
          minRows={MESSAGE_MIN_ROWS}
          maxLength={ROUTING_MESSAGE_MAX_LENGTH}
          isCounterVisible
          helperText={i18n.t('page.settings.telephony.spokenTextLengthHint')}
          isRequired
          isDisabled={props.isReadOnly}
          data-testid={suffixTestId('message', props)}
        />
        <Show when={!props.isReadOnly}>
          <HStack justify="flex-end">
            <Button
              variant="link"
              size="small"
              title={i18n.t('page.settings.telephony.routingCopyGreeting')}
              isDisabled={isNilOrEmpty(props.greetingOutOfHours)}
              onClick={handleCopyGreeting}
              data-testid={suffixTestId('copyGreeting', props)}
            />
          </HStack>
        </Show>
      </VStack>
    </Card>
  );
}
